import { Users, MessageCircle } from 'lucide-react';
import { useLang } from '@/lib/i18n';

interface ParticipantsListProps {
  participants: string[];
  myName: string;
  dmTarget?: string;
  onSelectDm: (name: string) => void;
}

/**
 * Pure participant-row list, shared by the desktop ParticipantsSidebar
 * and the mobile MobileParticipantsSheet so both stay in sync and never
 * drift apart in behavior or styling.
 */
export function ParticipantsList({ participants, myName, dmTarget, onSelectDm }: ParticipantsListProps) {
  const { t } = useLang();

  return (
    <>
      {participants.map((participant, i) => {
        const isMe = participant === myName;
        const isActive = dmTarget === participant;
        return (
          <button
            type="button"
            key={`${participant}-${i}`}
            onClick={() => !isMe && onSelectDm(isActive ? '' : participant)}
            className={`
              flex w-full items-center gap-3 px-3 py-2 rounded-lg transition-colors group text-left touch-manipulation
              ${isMe
                ? 'bg-sidebar-accent/50 text-sidebar-accent-foreground cursor-default'
                : isActive
                  ? 'bg-primary/20 text-foreground border border-primary/30 cursor-pointer'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent/30 cursor-pointer'
              }
            `}
          >
            <div className="relative flex-shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border shadow-sm
                ${isActive
                  ? 'bg-primary/30 text-foreground border-primary/40'
                  : 'bg-sidebar-primary/20 text-sidebar-foreground border-sidebar-border'
                }`}>
                {participant.charAt(0).toUpperCase()}
              </div>
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-success border-2 border-sidebar"></div>
            </div>
            <span className="text-sm font-medium truncate flex-1">
              {participant}
            </span>
            {isMe ? (
              <span className="text-[10px] uppercase tracking-wider text-sidebar-foreground/50 font-bold">
                {t('sidebar.you')}
              </span>
            ) : (
              <MessageCircle className={`h-3.5 w-3.5 flex-shrink-0 transition-opacity
                ${isActive ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-60 text-sidebar-foreground'}`}
              />
            )}
          </button>
        );
      })}
      {participants.length === 0 && (
        <div className="text-sm text-sidebar-foreground/50 p-3 text-center italic">
          {t('sidebar.gathering')}
        </div>
      )}
    </>
  );
}

interface ParticipantsSidebarProps {
  participants: string[];
  count: number;
  myName: string;
  dmTarget?: string;
  onSelectDm: (name: string) => void;
}

export function ParticipantsSidebar({ participants, count, myName, dmTarget, onSelectDm }: ParticipantsSidebarProps) {
  const { t } = useLang();

  return (
    <aside className="hidden md:flex flex-col w-64 border-r border-border bg-sidebar/80 backdrop-blur-xl h-full">
      <div className="p-4 border-b border-border flex items-center gap-2 text-sidebar-foreground">
        <Users className="h-4 w-4 opacity-70" />
        <h2 className="text-sm font-medium">{t('sidebar.inRoom')}</h2>
        <span className="ml-auto bg-sidebar-accent text-sidebar-accent-foreground text-xs px-2 py-0.5 rounded-full font-medium">
          {count}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        <ParticipantsList participants={participants} myName={myName} dmTarget={dmTarget} onSelectDm={onSelectDm} />
      </div>

      <div className="p-4 text-xs text-sidebar-foreground/40 border-t border-border mt-auto font-medium text-center">
        {t('sidebar.footer')}
      </div>
    </aside>
  );
}
